// VMS 即時事件串流（platform internal/vmsstream：GET /v1/vms/stream，WSS；plan/vms-web-v0.md §8）。
//
//   → signin {access_token}   ← signin_ok {user_id, token_exp, renew_supported} | signin_error{reason}
//   → subscribe {homes}       ← subscribed {homes, denied}
//   ← event {home_id, device_ref, event}   ← home_revoked {home_id}
//   ← alarm_state {home_id, event_id, device_ref, state, from, by, by_name?, note?, at, version, kind?, assigned_to?, assigned_to_name?}
//     （VMS 最小警報處理 SPEC EVT-06；kind / assigned_to* 是警報工作台 EVT-07 #vms-web-alarm-workbench 加的）
//   ← presence {home_id, device_ref, state, reason?, at}（在線狀態變了；NVR 通道沒有自己的，跟 parent。SPEC W24）
//   ← job {home_id, job_id, kind, state, progress, error?}（伺服器端工作進度，只推給建立的人；目前只有 kind=export。SPEC W24）
//
// 斷線自動重連（1 → 30 秒退避）；signin 被拒（token 過期）先 refresh 再連；家戶清單變了就重送 subscribe。
// 租期跟媒體信令同一套（lease.ts，vms-system §5.1）：收到 lease_expiring → refresh → 同一條連線送 renew；
// 沒續到、伺服端強制（media.enforce_token_expiry）時以 1008 token_expired 關線，這裡照一般斷線重連。
import { refresh, session } from './auth';
import { planeBase } from './config';
import { KinioError } from './errors';
import type { KinioEvent } from './events';
import { LEASE_RENEW_RETRY_MS, leaseFrameError, leaseRenewRetryable, leaseToken } from './lease';

/** renew 等回覆的上限。 */
export const STREAM_RENEW_TIMEOUT_MS = 30_000;

export interface StreamEvent {
  homeId: string;
  /** Thing 的定址鍵（通道 = `<parent>:chN`）。 */
  deviceRef: string;
  event: KinioEvent & { event_class?: string; sub?: string };
}

/** 警報處理狀態（SPEC EVT-06 #vms-web-alarm-handling）。 */
export type AlarmState = 'open' | 'acknowledged' | 'claimed' | 'resolved' | 'false_alarm';

/**
 * alarm_state 幀是哪一種變更（警報工作台 EVT-07，同 `vms.alarm_state_events.kind`）：`state` 換了狀態、`assign` 指派（帶被指派者）、
 * `unassign` 取消指派、`note` 只加備註、`overdue` 超過接手時限（`version` 不變）。
 */
export type AlarmFrameKind = 'state' | 'assign' | 'unassign' | 'note' | 'overdue';

const ALARM_FRAME_KINDS: readonly AlarmFrameKind[] = ['state', 'assign', 'unassign', 'note', 'overdue'];

/** 幀的 `kind`：沒帶（舊平台）或不是五種之一 → `state`（SPEC #vms-web-alarm-workbench；不丟幀，清單重讀會校正）。 */
export function alarmFrameKind(v: unknown): AlarmFrameKind {
  return ALARM_FRAME_KINDS.includes(v as AlarmFrameKind) ? (v as AlarmFrameKind) : 'state';
}

/** alarm_state 幀：某筆警報的處理狀態變了（全家戶 / 場域共用；依 event.read 過濾）。 */
export interface AlarmStateFrame {
  homeId: string;
  eventId: string;
  deviceRef: string;
  state: AlarmState;
  from: AlarmState;
  /** 改的人（user id）與顯示名 */
  by: string;
  byName?: string;
  note?: string;
  /** RFC 3339 */
  at: string;
  /** 這次變更之後的版本（每次 +1，非負整數）。前端只套比本地新的、下次改狀態帶 expected_version（plan#36）。沒帶 / 不合法的幀直接丟掉 */
  version: number;
  /** 哪一種變更（解析後一定有；自己組的幀可省略 = `state`）。 */
  kind?: AlarmFrameKind;
  /** 被指派的人（user id）與顯示名：只有 `kind = assign` 時帶。 */
  assignedTo?: string;
  assignedToName?: string;
}

/** 設備在線狀態（presence 文件的 state）。 */
export type PresenceState = 'online' | 'dormant' | 'asleep' | 'offline';

/** presence 幀：某台設備的在線狀態變了（依 event.read 過濾；同一台狀態沒變不重推）。 */
export interface PresenceFrame {
  homeId: string;
  deviceRef: string;
  state: PresenceState;
  /** 離線原因（keepalive_timeout / gateway_lost …）；只有 offline 才有 */
  reason?: string;
  /** RFC 3339 */
  at: string;
}

export type JobState = 'queued' | 'running' | 'succeeded' | 'failed';

/** job 幀：伺服器端工作的狀態或進度變了（只推給建立的人）。 */
export interface JobFrame {
  homeId: string;
  jobId: string;
  /** 目前只有 export（ONVIF 探索是同步回應，沒有工作） */
  kind: 'export';
  state: JobState;
  /** 0–100 */
  progress: number;
  /** 失敗的錯誤碼（UPPER_SNAKE） */
  error?: string;
}

export type StreamState = 'idle' | 'connecting' | 'open' | 'closed';

export interface VmsStreamOptions {
  apiEndpoint: string;
  onEvent(e: StreamEvent): void;
  onState?(s: StreamState): void;
  onRevoked?(homeId: string): void;
  onSubscribed?(homes: string[], denied: string[]): void;
  /** 警報處理狀態變更（EVT-06）；不給就忽略這種幀。 */
  onAlarmState?(a: AlarmStateFrame): void;
  /** 在線狀態變更（W24）；不給就忽略這種幀。 */
  onPresence?(p: PresenceFrame): void;
  /** 工作進度（W24）；不給就忽略這種幀。 */
  onJob?(j: JobFrame): void;
  /** 測試注入 */
  WebSocketImpl?: typeof WebSocket;
}

export function streamUrl(apiEndpoint: string): string {
  let base = planeBase(apiEndpoint);
  if (base.startsWith('/')) base = (globalThis.location?.origin ?? '') + base; // dev proxy 是同源路徑
  return base.replace(/^http/, 'ws') + '/v1/vms/stream';
}

export class VmsStream {
  private ws: WebSocket | null = null;
  private homes: string[] = [];
  private backoff = 1000;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;
  private refreshed = false;
  private renewing: Promise<void> | null = null;
  private renewReq: { id: string; ws: WebSocket; resolve(exp: number): void; reject(e: unknown): void; timer: ReturnType<typeof setTimeout> } | null = null;
  private renewRetry: ReturnType<typeof setTimeout> | null = null;
  private seq = 0;
  state: StreamState = 'idle';
  /** 目前連線的租期（伺服端的 token_exp，unix 秒）；0 = 不知道。 */
  tokenExp = 0;

  constructor(private o: VmsStreamOptions) {}

  private set(s: StreamState) {
    this.state = s;
    this.o.onState?.(s);
  }

  start() {
    this.stopped = false;
    this.connect();
  }

  stop() {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.dropRenew();
    this.ws?.close();
    this.ws = null;
    this.set('closed');
  }

  /** 換訂閱的家戶（整批取代）。 */
  subscribe(homes: string[]) {
    this.homes = [...new Set(homes)].sort();
    if (this.state === 'open') this.ws?.send(JSON.stringify({ type: 'subscribe', homes: this.homes }));
  }

  private connect() {
    const token = session.accessToken();
    if (!token || this.stopped) return;
    const WS = this.o.WebSocketImpl ?? WebSocket;
    this.set('connecting');
    const ws = new WS(streamUrl(this.o.apiEndpoint));
    this.ws = ws;
    ws.onopen = () => ws.send(JSON.stringify({ type: 'signin', access_token: token }));
    ws.onmessage = (m) => {
      let f: Record<string, unknown>;
      try {
        f = JSON.parse(String(m.data));
      } catch {
        return;
      }
      switch (f.type) {
        case 'signin_ok':
          this.backoff = 1000;
          this.refreshed = false;
          this.tokenExp = typeof f.token_exp === 'number' ? f.token_exp : 0;
          this.set('open');
          if (this.homes.length) ws.send(JSON.stringify({ type: 'subscribe', homes: this.homes }));
          break;
        case 'signin_error':
          if (f.reason === 'UNAUTHORIZED' && !this.refreshed) {
            this.refreshed = true; // token 過期：refresh 一次立刻重連
            ws.onclose = null;
            ws.close();
            refresh()
              .then(() => this.connect())
              .catch(() => this.retry());
            return;
          }
          ws.close();
          break;
        case 'subscribed':
          this.o.onSubscribed?.((f.homes as string[]) ?? [], (f.denied as string[]) ?? []);
          break;
        case 'event':
          this.o.onEvent({ homeId: String(f.home_id), deviceRef: String(f.device_ref), event: f.event as StreamEvent['event'] });
          break;
        case 'home_revoked':
          this.o.onRevoked?.(String(f.home_id));
          break;
        case 'alarm_state':
          // version 必帶（plan#36，不留舊格式）：沒帶或不是非負整數 = 違約幀，丟掉（清單每分鐘重撈會補上）
          if (!Number.isSafeInteger(f.version) || (f.version as number) < 0) break;
          this.o.onAlarmState?.({
            homeId: String(f.home_id),
            eventId: String(f.event_id),
            deviceRef: String(f.device_ref),
            state: f.state as AlarmState,
            from: f.from as AlarmState,
            by: String(f.by ?? ''),
            byName: typeof f.by_name === 'string' ? f.by_name : undefined,
            note: typeof f.note === 'string' ? f.note : undefined,
            at: String(f.at),
            version: f.version as number,
            kind: alarmFrameKind(f.kind),
            assignedTo: typeof f.assigned_to === 'string' && f.assigned_to ? f.assigned_to : undefined,
            assignedToName: typeof f.assigned_to_name === 'string' && f.assigned_to_name ? f.assigned_to_name : undefined,
          });
          break;
        case 'presence':
          this.o.onPresence?.({
            homeId: String(f.home_id),
            deviceRef: String(f.device_ref),
            state: f.state as PresenceState,
            reason: typeof f.reason === 'string' && f.reason ? f.reason : undefined,
            at: String(f.at),
          });
          break;
        case 'job':
          this.o.onJob?.({
            homeId: String(f.home_id),
            jobId: String(f.job_id),
            kind: f.kind as JobFrame['kind'],
            state: f.state as JobState,
            progress: typeof f.progress === 'number' ? f.progress : 0,
            error: typeof f.error === 'string' && f.error ? f.error : undefined,
          });
          break;
        case 'lease_expiring':
          void this.renewLease(typeof f.token_exp === 'number' ? f.token_exp : this.tokenExp);
          break;
        case 'response':
          if (f.command === 'renew' && this.renewReq && this.renewReq.ws === ws && f.request_id === this.renewReq.id) {
            const p = f.payload as { token_exp?: unknown } | undefined;
            const r = this.renewReq;
            this.renewReq = null;
            clearTimeout(r.timer);
            if (f.status !== undefined && f.status !== 'ok') r.reject(new KinioError(409, String(f.status).toUpperCase(), `renew: ${String(f.status)}`));
            else r.resolve(typeof p?.token_exp === 'number' ? p.token_exp : 0);
          }
          break;
        case 'error':
          if (this.renewReq && this.renewReq.ws === ws && f.request_id === this.renewReq.id) {
            const r = this.renewReq;
            this.renewReq = null;
            clearTimeout(r.timer);
            r.reject(leaseFrameError(f));
          }
          break; // 沒有 request_id 的 token_expired：伺服端接著關線，onclose 重連
      }
    };
    ws.onclose = () => {
      if (this.ws === ws) this.ws = null;
      this.dropRenew();
      this.retry();
    };
    ws.onerror = () => {
      /* onclose 會接著來 */
    };
  }

  /**
   * 續租（vms-system §5.1，同媒體信令）：refresh 拿新 access token，在同一條連線送 renew。單飛；
   * 暫時性失敗且租期（tokenExp，unix 秒）還沒到就隔 LEASE_RENEW_RETRY_MS 再試。
   */
  renewLease(tokenExp = this.tokenExp): Promise<void> {
    if (this.renewing) return this.renewing;
    this.renewing = (async () => {
      const ws = this.ws;
      if (!ws || this.state !== 'open') return;
      try {
        const token = await leaseToken(tokenExp);
        if (this.ws !== ws || this.state !== 'open') return; // refresh 期間斷線了：重連會用新 token signin
        const exp = await this.sendRenew(ws, token);
        if (exp > 0 && this.ws === ws) this.tokenExp = exp;
      } catch (e) {
        if (!this.stopped && this.ws === ws && leaseRenewRetryable(e, tokenExp)) {
          if (this.renewRetry) clearTimeout(this.renewRetry);
          this.renewRetry = setTimeout(() => {
            this.renewRetry = null;
            void this.renewLease(tokenExp);
          }, LEASE_RENEW_RETRY_MS);
        }
      }
    })().finally(() => {
      this.renewing = null;
    });
    return this.renewing;
  }

  private sendRenew(ws: WebSocket, token: string): Promise<number> {
    const id = `renew-${Date.now().toString(36)}-${++this.seq}`;
    return new Promise<number>((resolve, reject) => {
      const timer = setTimeout(() => {
        if (this.renewReq?.id === id) this.renewReq = null;
        reject(new KinioError(408, 'COMMAND_TIMEOUT', 'renew timed out'));
      }, STREAM_RENEW_TIMEOUT_MS);
      this.renewReq = { id, ws, resolve, reject, timer };
      try {
        ws.send(JSON.stringify({ type: 'renew', request_id: id, access_token: token }));
      } catch (e) {
        this.renewReq = null;
        clearTimeout(timer);
        reject(KinioError.network(e));
      }
    });
  }

  /** 連線沒了：等回覆的 renew 失敗、排好的重試取消（新連線 signin 會帶新的租期）。 */
  private dropRenew() {
    if (this.renewRetry) clearTimeout(this.renewRetry);
    this.renewRetry = null;
    const r = this.renewReq;
    this.renewReq = null;
    if (r) {
      clearTimeout(r.timer);
      r.reject(new KinioError(0, 'NETWORK', 'stream closed'));
    }
  }

  private retry() {
    if (this.stopped) return;
    this.set('closed');
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => this.connect(), this.backoff);
    this.backoff = Math.min(this.backoff * 2, 30_000);
  }
}
